import React from 'react';
import PropTypes from 'prop-types';
import HiddenText from '../HiddenText';
import GameStatusBadge from '../GameStatusBadge';
import LivePulseValue from '../LivePulseValue';
import { formatDateTime } from '../../utils/timeUtils';
import {
  getLockStatusClassName,
  formatLockStatusLabel,
  formatLiveGameClock,
  isLiveGameStatus,
  isLiveLockStatus,
  shouldDisplayLockStatus,
} from '../../utils/lockStatusUtils';
import { isMetricEmpty } from '../../utils/lockOverviewUtils';
import '../LockOverview/LockOverview.css';
import '../LivePulse.css';
import styles from './MobileLockCard.module.css';

const MobileLockCard = ({
  lock,
  selectedWeek,
  visibility,
  isUsernameClickable,
  onUsernameClick,
  getSimpsonsName,
}) => {
  const { shouldBlurUser, shouldBlurLockData, shouldBlurEverything } = visibility;

  const isLive = isLiveGameStatus(lock?.gameStatus);
  const isEmpty = isMetricEmpty(lock);
  const statusLabel = lock?.status ? formatLockStatusLabel(lock.status) : '-';
  const statusClass = getLockStatusClassName(lock?.status);
  const pulseStatus = isLiveLockStatus(lock?.status);

  const renderUsername = () => {
    if (shouldBlurEverything || shouldBlurUser) {
      return (
        <HiddenText
          text={getSimpsonsName(lock.username, selectedWeek)}
          tooltip="Nice try! User identity hidden until you make your pick."
          showTooltip
        />
      );
    }

    if (isUsernameClickable) {
      return (
        <button type="button" className={styles.usernameButton} onClick={() => onUsernameClick(lock.username)}>
          {lock.username}
        </button>
      );
    }

    return <span className={styles.username}>{lock.username}</span>;
  };

  const renderLockDetails = () => {
    if (shouldBlurEverything || (shouldBlurLockData && !isEmpty)) {
      return (
        <div className={styles.blurredBlock}>
          <HiddenText text="Jets +5 vs Sharks" tooltip="Nice try! Lock details hidden until you make your pick." showTooltip />
        </div>
      );
    }

    if (lock.lock === 'No Pick') {
      return <div className={styles.noPick}>No Pick</div>;
    }

    if (lock.lock === 'Bye Week') {
      return <div className={styles.byeWeek}>Bye Week</div>;
    }

    if (lock.lock === 'TBD' || !lock.lock) {
      return <div className={styles.noPick}>TBD</div>;
    }

    const clockLabel = formatLiveGameClock(lock.gamePeriod, lock.gameClock);
    const kickoffDisplay = clockLabel && isLive ? clockLabel : formatDateTime(lock.kickoff);

    const renderMetric = (value) => {
      if (value === null || value === undefined || value === '-') return '-';
      if (!isLive) return value;
      return (
        <LivePulseValue value={String(value)}>{value}</LivePulseValue>
      );
    };

    return (
      <>
        <div className={styles.pickLine}>{lock.lock}</div>
        {lock.matchup && <div className={styles.matchup}>{lock.matchup}</div>}
        <div className={styles.timingRow}>
          {isLive && clockLabel ? (
            <LivePulseValue value={clockLabel} className="live-game-clock">
              {kickoffDisplay}
            </LivePulseValue>
          ) : (
            <span>{kickoffDisplay}</span>
          )}
          <GameStatusBadge gameStatus={lock.gameStatus} kickoff={lock.kickoff} />
        </div>
        <div className={styles.metricsRow}>
          <span>Score {renderMetric(lock.score)}</span>
          <span>Total {renderMetric(lock.total)}</span>
          <span>Margin {renderMetric(lock.margin)}</span>
        </div>
        {lock.leadTime && lock.leadTime !== '-' && (
          <div className={styles.leadTime}>Locked {lock.leadTime} early</div>
        )}
      </>
    );
  };

  const renderStatus = () => {
    if (!shouldDisplayLockStatus(lock)) {
      return null;
    }

    if (shouldBlurEverything || (shouldBlurLockData && !isEmpty)) {
      return (
        <HiddenText text="W'ing" tooltip="Nice try! Lock details hidden until you make your pick." showTooltip />
      );
    }

    if (!lock?.status || statusLabel === '-') {
      return <span className={styles.statusUnknown}>-</span>;
    }

    const className = pulseStatus ? `${statusClass} live-status-pulse` : statusClass;

    if (isLive || pulseStatus) {
      return (
        <LivePulseValue value={statusLabel}>
          <span className={className}>{statusLabel}</span>
        </LivePulseValue>
      );
    }

    return <span className={className}>{statusLabel}</span>;
  };

  return (
    <article className={styles.card}>
      <div className={styles.cardHeader}>
        {renderUsername()}
        <div className={styles.statusSlot}>{renderStatus()}</div>
      </div>
      <div className={styles.cardBody}>{renderLockDetails()}</div>
    </article>
  );
};

MobileLockCard.propTypes = {
  lock: PropTypes.object.isRequired,
  selectedWeek: PropTypes.number.isRequired,
  visibility: PropTypes.shape({
    shouldBlurUser: PropTypes.bool,
    shouldBlurLockData: PropTypes.bool,
    shouldBlurEverything: PropTypes.bool,
  }).isRequired,
  isUsernameClickable: PropTypes.bool.isRequired,
  onUsernameClick: PropTypes.func.isRequired,
  getSimpsonsName: PropTypes.func.isRequired,
};

export default MobileLockCard;
